import { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import MDTypography from "components/MDTypography";

const today = () => new Date().toISOString().split("T")[0];

const money = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const toId = (value) => String(value || "");

function FeeReceiptMultiple() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem("user") || "{}");
    } catch (_error) {
      return {};
    }
  }, []);
  const clientId = user?.client_id;
  const collegeId = user?.college_id;

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchRollNo, setSearchRollNo] = useState("");
  const [studentMatches, setStudentMatches] = useState([]);
  const [feeRows, setFeeRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [paymentRows, setPaymentRows] = useState({});
  const [receiptDate, setReceiptDate] = useState(today());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const authHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem("token")}`,
    "Content-Type": "application/json",
  });

  const showMessage = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const resetPayments = () => {
    setFeeRows([]);
    setSelectedIds([]);
    setPaymentRows({});
  };

  const loadStudentFees = async (student) => {
    if (!student?.student_id) return;
    setLoading(true);
    resetPayments();
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/student/${student.student_id}/fee-books`,
        { headers: authHeaders() }
      );
      const rows = (data?.data || []).map((book, index) => {
        const due = Number(book.due_amount || 0);
        return {
          id: toId(book.fee_book_id),
          sno: index + 1,
          fee_book_id: toId(book.fee_book_id),
          feeHead: book.feeHead?.fee_head_name || "Fee",
          payType: book.feeHead?.pay_type || "",
          academicYear: book.feeHead?.academic_year || "",
          totalFee: Number(book.total_fee || book.fee_amount || 0),
          concession: Number(book.concession_amount || 0),
          refund: Number(book.refund_amount || 0),
          paid: Number(book.paid_amount || 0),
          due,
        };
      });
      setFeeRows(rows);
      setPaymentRows(
        Object.fromEntries(
          rows.map((row) => [
            row.id,
            {
              amount: row.due,
              payment_mode: "CASH",
              trn_ref_no: "",
              trn_ref_date: today(),
              remarks: "",
            },
          ])
        )
      );
      if (rows.length === 0) showMessage("No pending fee dues for this student", "info");
    } catch (error) {
      showMessage(error.response?.data?.message || "Unable to load fee dues", "error");
    } finally {
      setLoading(false);
    }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    setSearchRollNo(student?.student_rollno || "");
    setStudentMatches([]);
    await loadStudentFees(student);
  };

  const searchByRollNo = async () => {
    const searchText = searchRollNo.trim();
    if (!searchText) {
      showMessage("Enter roll number or student name", "warning");
      return;
    }
    setLoading(true);
    resetPayments();
    setSelectedStudent(null);
    setStudentMatches([]);
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/students/search`,
        {
          headers: authHeaders(),
          params: { search: searchText },
        }
      );
      const matches = data?.data || [];
      if (matches.length === 0) {
        showMessage("Student not found", "error");
        return;
      }
      if (matches.length === 1) {
        await selectStudent(matches[0]);
        return;
      }
      setStudentMatches(matches);
      showMessage("Multiple students found. Select one from the list.", "info");
    } catch (error) {
      showMessage(error.response?.data?.message || "Student not found", "error");
    } finally {
      setLoading(false);
    }
  };

  const updatePaymentRow = (id, field, value) => {
    setPaymentRows((current) => ({
      ...current,
      [id]: {
        ...current[id],
        [field]: value,
        ...(field === "payment_mode" && value === "CASH"
          ? { trn_ref_no: "", trn_ref_date: today() }
          : {}),
      },
    }));
  };

  const selectedRows = feeRows.filter((row) => selectedIds.includes(row.id));
  const selectedTotal = selectedRows.reduce(
    (sum, row) => sum + Number(paymentRows[row.id]?.amount || 0),
    0
  );

  const validatePayments = () => {
    if (!selectedStudent) return "Select a student first";
    if (selectedRows.length === 0) return "Select at least one fee row";
    for (const row of selectedRows) {
      const payment = paymentRows[row.id] || {};
      const amount = Number(payment.amount || 0);
      if (!Number.isFinite(amount) || amount <= 0) {
        return `Enter a valid amount for ${row.feeHead}`;
      }
      if (amount > row.due) {
        return `Amount cannot exceed due for ${row.feeHead}`;
      }
      if (!payment.payment_mode) return `Select payment mode for ${row.feeHead}`;
      if (payment.payment_mode !== "CASH" && !String(payment.trn_ref_no || "").trim()) {
        return `Enter transaction reference for ${row.feeHead}`;
      }
    }
    return "";
  };

  const openPdfPreview = async (receiptIds) => {
    const { data } = await axios.get(
      `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}/preview-pdf`,
      {
        headers: authHeaders(),
        params: { receiptIds: receiptIds.join(",") },
        responseType: "blob",
      }
    );
    const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  };

  const submitPayments = async () => {
    const validationMessage = validatePayments();
    if (validationMessage) {
      showMessage(validationMessage, "warning");
      return;
    }

    const confirm = await Swal.fire({
      title: "Confirm payment?",
      text: `Do you want to confirm payment of Rs. ${money(selectedTotal)} for ${
        selectedRows.length
      } fee item(s)?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Confirm payment?",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const payload = {
        student_id: selectedStudent.student_id,
        receipt_date: receiptDate,
        created_by: user?.user_email || "",
        payments: selectedRows.map((row) => {
          const payment = paymentRows[row.id];
          return {
            fee_book_id: row.fee_book_id,
            amount: Number(payment.amount),
            payment_mode: payment.payment_mode,
            trn_ref_no: payment.payment_mode === "CASH" ? "" : payment.trn_ref_no,
            trn_ref_date: payment.payment_mode === "CASH" ? null : payment.trn_ref_date,
            remarks: payment.remarks || "",
          };
        }),
      };

      const { data } = await axios.post(
        `${API_BASE_URL}/api/feereceipt-multiple/${clientId}/${collegeId}`,
        payload,
        { headers: authHeaders() }
      );

      const receiptIds = (data?.data || []).map((item) => item.fee_receipt_id).filter(Boolean);

      showMessage("Payment saved successfully", "success");
      await openPdfPreview(receiptIds);
      await loadStudentFees(selectedStudent);
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Unable to save payment, Verify details",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { field: "sno", headerName: "S.No", width: 70 },
    {
      field: "feeHead",
      headerName: "Fee Head",
      minWidth: 360,
      flex: 2,
      renderCell: (params) => (
        <Box sx={{ whiteSpace: "normal", lineHeight: 1.35, py: 1 }}>{params.value}</Box>
      ),
    },
    /*   { field: "payType", headerName: "Pay Type", minWidth: 105, flex: 0.6 },
    { field: "academicYear", headerName: "Academic Year", minWidth: 120, flex: 0.7 }, */
    {
      field: "totalFee",
      headerName: "Total",
      minWidth: 95,
      renderCell: (params) => money(params.value),
    },
    {
      field: "concession",
      headerName: "Concession",
      minWidth: 115,
      renderCell: (params) => money(params.value),
    },
    {
      field: "paid",
      headerName: "Paid",
      minWidth: 95,
      renderCell: (params) => money(params.value),
    },
    {
      field: "due",
      headerName: "Due",
      minWidth: 145,
      flex: 0.8,
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            color: "#7f1d1d",
            bgcolor: "#fee2e2",
            border: "1px solid #991b1b",
            borderRadius: 1,
            fontWeight: 800,
            px: 1,
            py: 0.5,
            lineHeight: 1.2,
            textAlign: "right",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
          }}
        >
          {money(params.value)}
        </Box>
      ),
    },
    {
      field: "amount",
      headerName: "Pay Amount",
      minWidth: 130,
      sortable: false,
      renderCell: (params) => (
        <TextField
          type="number"
          size="medium"
          value={paymentRows[params.row.id]?.amount || ""}
          inputProps={{ min: 0, max: params.row.due, step: "1.00" }}
          onChange={(event) => updatePaymentRow(params.row.id, "amount", event.target.value)}
          sx={{
            width: "100%",
            bgcolor: "#fee2e2",
            "& .MuiInputBase-input": {
              fontSize: "17px", // Increase font size
              fontWeight: 800, // Bold text
              color: "#0c0cd2",
              textAlign: "center",
            },
          }}
        />
      ),
    },
    {
      field: "mode",
      headerName: "Mode",
      minWidth: 125,
      sortable: false,
      renderCell: (params) => (
        <TextField
          select
          size="medium"
          fullWidth
          value={paymentRows[params.row.id]?.payment_mode || "CASH"}
          onChange={(event) => updatePaymentRow(params.row.id, "payment_mode", event.target.value)}
        >
          <MenuItem value="CASH">Cash</MenuItem>
          <MenuItem value="ONLINE">Online</MenuItem>
          <MenuItem value="DD">DD</MenuItem>
          <MenuItem value="CHEQUE">Cheque</MenuItem>
        </TextField>
      ),
    },
    {
      field: "ref",
      headerName: "txn_ref_no",
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const payment = paymentRows[params.row.id] || {};
        return (
          <TextField
            size="small"
            disabled={payment.payment_mode === "CASH"}
            value={payment.trn_ref_no || ""}
            onChange={(event) => updatePaymentRow(params.row.id, "trn_ref_no", event.target.value)}
          />
        );
      },
    },
    {
      field: "refDate",
      headerName: "Ref Date",
      minWidth: 145,
      sortable: false,
      renderCell: (params) => {
        const payment = paymentRows[params.row.id] || {};
        return (
          <TextField
            type="date"
            size="small"
            disabled={payment.payment_mode === "CASH"}
            value={payment.trn_ref_date || today()}
            inputProps={{ max: today() }}
            onChange={(event) =>
              updatePaymentRow(params.row.id, "trn_ref_date", event.target.value)
            }
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 2 }}>
      {/*  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
        <ReceiptLongIcon color="primary" />
        <MDTypography variant="h5">Collect Multiple Fee </MDTypography>
      </Stack> */}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Roll Number / Student Name"
              fullWidth
              size="small"
              value={searchRollNo}
              onChange={(event) => setSearchRollNo(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") searchByRollNo();
              }}
            />
          </Grid>
          <Grid item xs={12} md={1.3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={searchByRollNo}
              sx={{ color: "#fff" }}
            >
              Search
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Receipt Date"
              type="date"
              fullWidth
              size="small"
              value={receiptDate}
              inputProps={{ max: today() }}
              onChange={(event) => setReceiptDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PrintIcon />}
              disabled={saving || selectedRows.length === 0}
              onClick={submitPayments}
              sx={{ color: "#fff" }}
            >
              {saving ? "Saving" : "Pay"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {studentMatches.length > 0 && (
        <Paper variant="outlined" sx={{ height: 260, width: "100%", mb: 2 }}>
          <DataGrid
            rows={studentMatches.map((student) => ({
              ...student,
              id: toId(student.student_id),
              branchName: student.branch?.branch_name || "",
              batchName: student.admittedbatch?.admittedbatch || "",
            }))}
            columns={[
              { field: "student_rollno", headerName: "Roll No", minWidth: 140, flex: 0.8 },
              { field: "student_name", headerName: "Student Name", minWidth: 220, flex: 1.5 },
              { field: "branchName", headerName: "Branch", minWidth: 160, flex: 1 },
              { field: "batchName", headerName: "Batch", minWidth: 120, flex: 0.8 },
              {
                field: "select",
                headerName: "Select",
                width: 110,
                sortable: false,
                renderCell: (params) => (
                  <Button
                    size="small"
                    variant="contained"
                    sx={{ color: "#fff" }}
                    onClick={() => selectStudent(params.row)}
                  >
                    Select
                  </Button>
                ),
              },
            ]}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableSelectionOnClick
          />
        </Paper>
      )}

      {selectedStudent && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: "#fcf8fa" }}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Student Name
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#1976d2", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedStudent.student_name}
              </MDTypography>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Roll No
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#2e7d32", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedStudent.student_rollno}
              </MDTypography>
            </Grid>

            <Grid item xs={6} md={2}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Selected FeeHeads
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#ed6c02", fontWeight: "bold", fontSize: "16px" }}
              >
                {selectedRows.length}
              </MDTypography>
            </Grid>

            <Grid item xs={12} md={3}>
              <MDTypography
                variant="caption"
                sx={{
                  fontSize: "14px", // change as needed
                  fontWeight: 600,
                }}
              >
                Payment Total
              </MDTypography>
              <MDTypography
                variant="button"
                display="block"
                sx={{ color: "#d32f2f", fontWeight: "bold", fontSize: "18px" }}
              >
                Rs. {money(selectedTotal)}
              </MDTypography>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ height: 530, width: "100%" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
            <CircularProgress />
          </Stack>
        ) : (
          <DataGrid
            rows={feeRows}
            columns={columns}
            checkboxSelection
            disableSelectionOnClick
            selectionModel={selectedIds}
            onSelectionModelChange={(newSelection) => {
              setSelectedIds(newSelection.map(String));
            }}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-cell": {
                alignItems: "flex-start",
                py: 1,
              },
              "& .MuiCheckbox-root": {
                color: "#000",
              },
              "& .MuiCheckbox-root .MuiSvgIcon-root": {
                border: "1.5px solid #000",
                borderRadius: "2px",
              },
              "& .MuiCheckbox-root.Mui-checked": {
                color: "#000",
              },
              "& .MuiCheckbox-root.MuiCheckbox-indeterminate": {
                color: "#000",
              },
            }}
          />
        )}
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FeeReceiptMultiple;
